import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CardAudience, CardDetailsInput, CardScanItem, CardScanMatch, CardScanSummary, SaveCardScanInput, SaveManyCardScansResult, UpdateCardScanInput } from '@crm/shared';
import { api } from '../../api/client';

type TagRef = { id: string; name: string; color: string | null };

function useInvalidate() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['card-scans'] }), qc.invalidateQueries({ queryKey: ['card-scan'] }), qc.invalidateQueries({ queryKey: ['card-scan-summary'] })]);
}

/** Open cards (being read, to check, failed), polled while any is still being read; or the ones already saved. */
export const useCardScans = (status: 'open' | 'saved' = 'open', enabled = true) =>
  useQuery({
    queryKey: ['card-scans', status],
    queryFn: async () => (await api.get<CardScanItem[]>('/card-scans', { params: { status } })).data,
    enabled,
    refetchInterval: (q) => (q.state.data?.some((s) => s.status === 'PROCESSING') ? 2_500 : false),
  });

/** Counts for the menu badge and the page header; polled slowly so cards read in the background show up. */
export const useCardScanSummary = (enabled: boolean) =>
  useQuery({
    queryKey: ['card-scan-summary'],
    queryFn: async () => (await api.get<CardScanSummary>('/card-scans/summary')).data,
    enabled,
    refetchInterval: (q) => (q.state.data?.processing ? 3_000 : 60_000),
  });

export const useCardScan = (id: string | undefined) =>
  useQuery({
    queryKey: ['card-scan', id],
    queryFn: async () => (await api.get<CardScanItem>(`/card-scans/${id}`)).data,
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === 'PROCESSING' ? 2_000 : false),
  });

export const useEventTags = () => useQuery({ queryKey: ['card-scan-event-tags'], queryFn: async () => (await api.get<TagRef[]>('/card-scans/event-tags')).data });

export const useAddedByTag = () => useQuery({ queryKey: ['card-scan-added-by'], queryFn: async () => (await api.get<TagRef>('/card-scans/added-by-tag')).data, staleTime: Infinity });

export function useCreateEventTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => (await api.post<TagRef>('/card-scans/event-tags', { name })).data,
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['card-scan-event-tags'] }), qc.invalidateQueries({ queryKey: ['tags'] })]),
  });
}

export function useUploadCardScan() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (input: { front: Blob; back: Blob | null; eventTagId: string | null; qrText: string | null } & CardAudience) => {
      const form = new FormData();
      form.append('front', input.front, 'front.jpg');
      if (input.back) form.append('back', input.back, 'back.jpg');
      if (input.eventTagId) form.append('eventTagId', input.eventTagId);
      if (input.qrText) form.append('qrText', input.qrText);
      for (const key of ['tagIds', 'listIds', 'segmentIds'] as const) if (input[key].length) form.append(key, input[key].join(','));
      return (await api.post<CardScanItem>('/card-scans', form)).data;
    },
    onSuccess: inv,
  });
}

export function useCardScanMatches(details: CardDetailsInput | null, enabled: boolean) {
  return useQuery({
    queryKey: ['card-scan-matches', details],
    queryFn: async () => (await api.post<CardScanMatch[]>('/card-scans/matches', details)).data,
    enabled: enabled && !!details,
    placeholderData: (prev) => prev,
  });
}

/** Where a card was collected, or the tags, lists and segments its contact goes into. */
export function useUpdateCardScan() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async ({ id, ...patch }: UpdateCardScanInput & { id: string }) => (await api.patch<CardScanItem>(`/card-scans/${id}`, patch)).data, onSuccess: inv });
}

/** Saves read cards as they were read: onto a contact with the same email or phone, else as new contacts. */
export function useSaveManyCardScans() {
  const inv = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => (await api.post<SaveManyCardScansResult>('/card-scans/save-many', { ids }, { timeout: 10 * 60_000 })).data,
    onSettled: () => Promise.all([inv(), qc.invalidateQueries({ queryKey: ['contacts'] }), qc.invalidateQueries({ queryKey: ['tags'] })]),
  });
}

export function useRetryCardScan() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.post<CardScanItem>(`/card-scans/${id}/retry`)).data, onSuccess: inv });
}

export function useDiscardCardScan() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.post<CardScanItem>(`/card-scans/${id}/discard`)).data, onSuccess: inv });
}

export function useSaveCardScan() {
  const inv = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: SaveCardScanInput & { id: string }) => (await api.post<{ contactId: string; merged: boolean }>(`/card-scans/${id}/save`, body)).data,
    onSuccess: () => Promise.all([inv(), qc.invalidateQueries({ queryKey: ['contacts'] }), qc.invalidateQueries({ queryKey: ['tags'] })]),
  });
}
